/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Media from 'react-media';

import { IconButton, breakpoints } from '@coursera/cds-core';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import PathwayContentCard from 'bundles/unified-common/components/recommended-pathway/PathwayContentCard';
import useCustomScroll from 'bundles/unified-common/hooks/useCustomScroll';

import _t from 'i18n!nls/consumer-home';

type CourseData = {
  promoPhotoUrl?: string | null;
  name: string;
};

type Props = {
  courseData: CourseData[];
  totalCourses: number;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: row;
  `,
  cardsAndArrowsContainer: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  divider: (scrolled: boolean) => css`
    box-shadow: ${scrolled ? 'inset 10px 0 8px -8px var(--cds-color-darken-100)' : 'none'};
    display: flex;
    flex-direction: column;
    height: 500px;
    position: absolute;
    top: 0;
    padding-left: var(--cds-spacing-100);
    z-index: 1;
  `,
  cardContainer: css`
    display: flex;
    overflow-x: hidden;
    scroll-behavior: auto;
    padding-right: var(--cds-spacing-300);
    gap: var(--cds-spacing-200);
    position: relative;

    &::-webkit-scrollbar {
      display: none;
    }

    ${breakpoints.down('sm')} {
      flex-direction: column;
      padding-top: var(--cds-spacing-50);
      gap: var(--cds-spacing-200);
      padding-left: 0;
      padding-right: 0;
    }
  `,
  arrowsContainer: css`
    display: flex;
    justify-content: flex-end;
    padding-right: var(--cds-spacing-200);
    padding-top: var(--cds-spacing-100);
  `,
  line: (width?: number) => css`
    position: absolute;
    top: 58px;
    transform: translateY(-50%);
    border: 2px solid var(--cds-color-neutral-disabled-strong);
    width: ${width}px;
    z-index: -1;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

const totalImageBlockWidth = 180;
const buffer = 24; // buffer for the extra space on the end

/**
 * @description This component is used to display the course progression in the pathway card.
 */
const PathwayCourseProgression = ({ courseData, totalCourses }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollPrevious, scrollNext, canScrollNext, canScrollPrev, scrolled } = useCustomScroll({
    containerRef,
    buffer,
    totalImageBlockWidth,
  });

  const displayCourseData = [...courseData];
  const userAgent = useUserAgent();

  return (
    <Media query={{ maxWidth: breakpoints.values.md - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
      {(isMobileScreen) => {
        if (isMobileScreen) {
          displayCourseData.length = 3; // show only 3 courses on mobile
        }
        return (
          <div css={styles.container}>
            <div css={styles.divider(!!scrolled)} />
            <div css={styles.cardsAndArrowsContainer}>
              <div ref={containerRef} css={styles.cardContainer}>
                <div css={styles.line(totalImageBlockWidth * displayCourseData.length - buffer)} />
                {displayCourseData.map((course, index) => (
                  <PathwayContentCard
                    key={course.name}
                    photoUrl={course.promoPhotoUrl}
                    title={course.name}
                    subtitle={
                      <FormattedMessage
                        // eslint-disable-next-line no-restricted-syntax
                        message={_t('Course {index} of {totalCourses}')}
                        index={index + 1}
                        totalCourses={totalCourses}
                      />
                    }
                    imgParams={{ fit: 'crop', auto: 'compress', width: 164, height: 109 }}
                  />
                ))}
              </div>
              {displayCourseData.length > 4 && (
                <div css={styles.arrowsContainer}>
                  <IconButton onClick={scrollPrevious} intent="previous" variant="ghost" disabled={!canScrollPrev} />
                  <IconButton onClick={scrollNext} intent="next" variant="ghost" disabled={!canScrollNext} />
                </div>
              )}
            </div>
          </div>
        );
      }}
    </Media>
  );
};

export default PathwayCourseProgression;
